// Catalog.ID Client — Organization Vouchers Pane
// ============================================================================
// The organization's delegate slots (registration-restructure §13.4): buy them
// in advance, then see what became of each one.
//
// Two things make this pane different from the operator backoffice next door:
//
//  1. It BUYS rather than mints. The purchase runs the ordinary Stripe popup
//     through `buyVouchers` — the same helper the anonymous `/vouchers` route
//     and both wizards use — and the conversion call carries the delegate
//     session, which is what makes the server record the batch against this
//     organization. There is no "which organization" field to get wrong: the
//     session says it, so a delegate cannot spend into a ledger that is not
//     theirs, and an owner cannot accidentally buy into a stranger's.
//
//  2. It shows STOCK, not a payment record. An organization sees the slots it
//     holds and what became of each one — available, in use, spent, withdrawn.
//     There is no payment identifier here at all, not even the opaque batch
//     reference the operator backoffice shows (registration-restructure §14.4):
//     the node does not track who paid, and an owner reconciling a card
//     statement does that at their payment provider, not in their delegate
//     ledger.
//
// Codes appear exactly once, right after a purchase — the server stores only
// hashes and there is no re-display path, which the copy states plainly.
//
// Owner/admin only, mirroring the server gate. The gate here is a convenience:
// `GET /api/organization/{orgCid}/vouchers` and the purchase binding are both
// checked server-side regardless of what this pane chooses to render.

import { createElement, createFieldGroup } from '@catalog-shell';
import type { PaneType, PaneTypeRenderContext } from '@catalog-shell';
import { formatApiError } from '../api/client';
import { listOrganizationVouchers } from '../api/voucher';
import type { OrgVoucherSummary } from '../api/voucher';
import {
    buyVouchers, fetchRegistrationConfig, isTestHookActive, type BitsBounds,
} from '../auth/payment-flow';
import { renderVoucherCodes } from '../auth/voucher-code-display';
import {
    validateBits, bitsEuroLabel, mintVoucherBatch, type MintedVoucherBatch,
} from '../auth/voucher-mint';
import {
    deriveVoucherSecrets, normalizeVoucherCode, voucherUrl, VOUCHER_CODE_PLACEHOLDER,
} from '../auth/voucher-code';
import { addStoredCodes } from '../state/voucher-code-store';
import { bitcashWorker } from '../state/bitcash-context';
import { unitsToBit } from '../api/bitcash-units';
import { lockKeyFor, displayWalletKey } from '../crypto/bitcash';
import { MAX_VOUCHER_QUANTITY } from '../api/payment';
import {
    buildVoucherCardSheet, type VoucherCardData,
} from '../components/booklet/voucher-card';
import { printPdf } from '../components/pdf-loader';
import { session } from '../state/session';
import { identity } from '../state/identity';
import { PANE_IDS } from '../shell-config';

const PAGE_SIZE = 50;

/** Roles that may commit the organization's money — the server's own gate. */
const PURCHASING_ROLES = ['owner', 'admin'];

// ── Pure helpers (unit-tested; no DOM, no network) ──────────────────────────

export type OrgVoucherLifecycle = 'available' | 'in use' | 'spent' | 'withdrawn';

/**
 * What a status means to an ORGANIZATION, as opposed to what it means to the
 * node. An org does not care that `refunded` and `revoked` arrived by different
 * routes — both mean the slot is gone — but it cares a great deal about the line
 * between "I can still invite someone with this" and "I cannot".
 */
export function lifecycleOf(status: OrgVoucherSummary['status']): OrgVoucherLifecycle {
    switch (status) {
        case 'unredeemed': return 'available';
        case 'reserved': return 'in use';
        case 'consumed': return 'spent';
        default: return 'withdrawn';
    }
}

/** Headline counts. `available` is the number of delegates that can still be
 *  invited without paying again, which is the only number an owner asks for. */
export function summarizeVouchers(rows: OrgVoucherSummary[]): Record<OrgVoucherLifecycle, number> {
    const counts: Record<OrgVoucherLifecycle, number> = {
        'available': 0, 'in use': 0, 'spent': 0, 'withdrawn': 0,
    };
    for (const row of rows) counts[lifecycleOf(row.status)]++;
    return counts;
}

export function summaryLine(counts: Record<OrgVoucherLifecycle, number>): string {
    return `${counts.available} available · ${counts['in use']} in use · `
        + `${counts.spent} spent · ${counts.withdrawn} withdrawn`;
}

/** True when this pane may be used at all — the client-side twin of the server
 *  gate, so a viewer or treasurer is told rather than shown a failing request. */
export function canManageVouchers(roles: { hasRole(role: string): boolean }): boolean {
    return PURCHASING_ROLES.some((r) => roles.hasRole(r));
}

/** How an expired-but-unredeemed row should read. Expiry is a date on the row,
 *  not a status the server writes, so the ledger has to work it out. */
export function isExpired(row: OrgVoucherSummary, now: Date = new Date()): boolean {
    return row.status === 'unredeemed' && new Date(row.expiresAt).getTime() < now.getTime();
}

// ── Pane ────────────────────────────────────────────────────────────────────

export function createOrgVouchersPane(): PaneType {
    return {
        typeId: PANE_IDS.orgVouchers,
        kind: 'leaf',
        defaultTitle: 'Vouchers',
        capabilities: [],
        render: (ctx: PaneTypeRenderContext) => {
            const container = createElement('div', { className: 'cid-pane-content' });
            void renderOrgVouchers(container, ctx);
            return container;
        },
    };
}

async function renderOrgVouchers(container: HTMLElement, _ctx: PaneTypeRenderContext): Promise<void> {
    container.innerHTML = '';

    const orgCid = identity.getOrganizationCid();
    if (!orgCid) {
        container.appendChild(createElement('p', {
            className: 'cid-pane-note',
            textContent: 'No organization is active in this session.',
        }));
        return;
    }
    if (!canManageVouchers(identity)) {
        container.appendChild(createElement('p', {
            className: 'cid-pane-note',
            textContent: 'Only owners and admins can see or buy the organization\'s delegate vouchers.',
        }));
        return;
    }

    const errorEl = createElement('div', { className: 'cid-pane-error' });
    container.appendChild(errorEl);

    const buySection = createFieldGroup({ title: 'Buy delegate vouchers' });
    container.appendChild(buySection.element);

    const checkSection = createFieldGroup({ title: 'Check a voucher' });
    container.appendChild(checkSection.element);
    renderCheckVoucher(checkSection.body);

    const listSection = createElement('div');
    container.appendChild(listSection);

    /** The node's announced site, for the QR a printed card carries. */
    let siteBaseUrl: string | null = null;

    // ── Buy ─────────────────────────────────────────────────────────────────

    const buyBody = createElement('div', { className: 'cid-wizard-step' });
    const intro = createElement('p', {
        className: 'cid-wizard-text',
        textContent: 'A delegate voucher pays for one delegate. Buy them ahead of time: creating an '
            + 'invitation reserves one, activation spends it, and a revoked or expired invitation '
            + 'gives it back. This is stock the organization holds, not a record of what was paid — '
            + 'codes bought here belong to this organization and can only be used for its own '
            + 'invitations.',
    });
    buyBody.appendChild(intro);

    const qtyRow = createElement('div', { className: 'cid-auth-field' });
    qtyRow.appendChild(createElement('label', {
        className: 'cid-wizard-text', textContent: 'How many',
    }));
    const qtyInput = createElement('input', { className: 'cid-auth-input' });
    qtyInput.type = 'number';
    qtyInput.min = '1';
    qtyInput.max = String(MAX_VOUCHER_QUANTITY);
    qtyInput.value = '1';
    qtyRow.appendChild(qtyInput);
    buyBody.appendChild(qtyRow);

    // ── bits (D1) ───────────────────────────────────────────────────────────
    // Every delegate voucher carries bits, and the organization chooses how
    // many: they become the new delegate's own spending money, and the
    // organization is paying for them by card. The node never learns the
    // figure, so it appears nowhere in the ledger below.
    let bitsBounds: BitsBounds = { min: 100_000, max: 10_000_000, default: 100_000 };
    let bitsPerEuro: number | null = null;

    const bitsRow = createElement('div', { className: 'cid-auth-field' });
    bitsRow.appendChild(createElement('label', {
        className: 'cid-wizard-text', textContent: 'Bits on each voucher',
    }));
    const bitsInput = createElement('input', { className: 'cid-auth-input' });
    bitsInput.type = 'number';
    bitsInput.step = '1';
    bitsRow.appendChild(bitsInput);
    const bitsHint = createElement('p', { className: 'cid-pane-note' });
    bitsRow.appendChild(bitsHint);
    buyBody.appendChild(bitsRow);

    function syncBitsHint(): void {
        bitsHint.textContent = bitsEuroLabel(Number(bitsInput.value) || 0, bitsPerEuro)
            + ' — spending money for the delegate this voucher activates. '
            + 'Unused bits return to the operator when the voucher expires.';
    }
    bitsInput.addEventListener('input', syncBitsHint);

    const buyBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes cid-auth-submit',
        textContent: isTestHookActive() ? 'Buy (test hook — skip Stripe)' : 'Continue to payment',
    });
    buyBody.appendChild(buyBtn);

    const statusEl = createElement('p', { className: 'cid-wizard-status' });
    buyBody.appendChild(statusEl);

    const codesArea = createElement('div');
    buyBody.appendChild(codesArea);
    buySection.body.appendChild(buyBody);

    // Runtime payment posture, exactly as the wizards and the /vouchers route
    // read it: a waived node hands delegates out free, so there is nothing to
    // sell. Fails CLOSED — an unreachable config endpoint keeps payment on, and
    // the control stays disabled until the answer arrives so a fast click cannot
    // start a checkout the node does not want.
    buyBtn.setAttribute('disabled', '');
    void (async () => {
        const cfg = await fetchRegistrationConfig();
        siteBaseUrl = cfg.siteBaseUrl;
        bitsBounds = cfg.bits;
        bitsInput.min = String(bitsBounds.min);
        bitsInput.max = String(bitsBounds.max);
        bitsInput.value = String(bitsBounds.default);
        syncBitsHint();
        if (!cfg.paymentRequired) {
            intro.textContent = 'Delegates are free on this server, so there is nothing to buy. '
                + 'Create invitations directly.';
            qtyRow.remove();
            bitsRow.remove();
            buyBtn.remove();
            return;
        }
        buyBtn.removeAttribute('disabled');
        // The pilot Worker's euro rate is gone with it (apps/bitcash/docs/plans/bitcash-v2.md M12).
        syncBitsHint();
    })();

    buyBtn.addEventListener('click', () => {
        const quantity = Number(qtyInput.value);
        if (!Number.isInteger(quantity) || quantity < 1 || quantity > MAX_VOUCHER_QUANTITY) {
            showError(`Choose a whole number of vouchers between 1 and ${MAX_VOUCHER_QUANTITY}.`);
            return;
        }
        const bits = Number(bitsInput.value);
        const bitsProblem = validateBits(bits, bitsBounds);
        if (bitsProblem) { showError(bitsProblem); return; }

        // Open the popup synchronously, inside the click's user activation and
        // before any await, or the browser may block it (same rule as the
        // wizard's Stripe popup and the /vouchers route). It sits blank while
        // the codes are minted.
        const popup = window.open('', 'cid-stripe-checkout', 'width=480,height=760');

        buyBtn.setAttribute('disabled', '');
        buyBtn.textContent = 'Waiting for payment…';
        errorEl.classList.remove('cid-pane-error--visible');
        statusEl.textContent = 'Preparing the codes…';

        void (async () => {
            // The codes are minted HERE, before the card is charged (§4.3) —
            // the webhook creates each voucher's lock against the key its code
            // derives, and has nothing to create it against otherwise.
            let batch: MintedVoucherBatch;
            try {
                batch = await mintVoucherBatch({
                    registrationType: 'delegate', quantity, bits,
                    onProgress: (done, total) => {
                        statusEl.textContent = total > 1
                            ? `Preparing the codes… (${done} of ${total})` : 'Preparing the code…';
                    },
                });
            } catch (e) {
                try { popup?.close(); } catch { /* already closed */ }
                showError(e instanceof Error ? e.message : 'Could not prepare the codes');
                return;
            }

            statusEl.textContent = 'Complete your payment in the Stripe window.';
            const result = await buyVouchers({
                popup,
                registrationType: 'delegate',
                quantity,
                bits,
                vouchers: batch.specs,
                onStatus: (label) => { statusEl.textContent = label; },
            });

            if (result.status === 'complete') {
                statusEl.textContent = '';
                qtyRow.remove();
                bitsRow.remove();
                buyBtn.remove();
                const codes = batch.vouchers.map(v => v.code);
                renderVoucherCodes(codesArea, codes, {
                    expiresAt: result.expiresAt,
                    headingTag: 'h4',
                    noun: 'delegate voucher',
                });
                // D19: the owner's device keeps them, because nothing else can.
                // A store that refuses (no local storage, a locked device) is
                // not an error — the codes are on screen and Print is one click
                // away — but the copy has to say which happened.
                const stored = await addStoredCodes(session.getInfo()?.cid, codes.map(code => ({
                    code, registrationType: 'delegate' as const, bits, expiresAt: result.expiresAt,
                })));
                renderCodeActions(codesArea, batch, result.expiresAt, stored);
                if (result.organizationCid !== orgCid) {
                    // The server reports what the ROWS say. A mismatch means the
                    // batch did not land in this ledger — a session that expired
                    // mid-checkout, or a replayed proof that belongs to someone
                    // else. The codes still work; they are just unbound stock, and
                    // saying so beats leaving an owner hunting an empty ledger.
                    codesArea.appendChild(createElement('p', {
                        className: 'cid-pane-note',
                        textContent: 'These codes were not recorded against this organization, so they '
                            + 'will not appear in the list below. They still work — keep them safe.',
                    }));
                }
                await refresh();
                return;
            }
            if (result.status === 'cancelled') {
                resetButton();
                statusEl.textContent = 'Payment cancelled. You can try again.';
                return;
            }
            resetButton();
            showError(result.error);
        })();
    });

    function resetButton(): void {
        buyBtn.removeAttribute('disabled');
        buyBtn.textContent = isTestHookActive() ? 'Buy (test hook — skip Stripe)' : 'Continue to payment';
    }

    /** Print, and the sentence about where these codes now live. */
    function renderCodeActions(
        area: HTMLElement, batch: MintedVoucherBatch, expiresAt: string, stored: boolean,
    ): void {
        const cards: VoucherCardData[] = batch.vouchers.map(v => ({
            code: v.code,
            registrationType: 'delegate',
            bits: batch.bits,
            url: voucherUrl(siteBaseUrl ?? window.location.origin, v.code),
            siteUrl: siteBaseUrl ?? window.location.origin,
        }));
        const actions = createElement('div', { className: 'cs-pane-toolbar' });
        const printBtn = createElement('button', {
            className: 'cs-btn cs-btn--yes cs-btn--small',
            textContent: cards.length === 1 ? 'Print voucher' : 'Print vouchers',
        });
        // Synchronous from the click: printPdf opens its window immediately.
        printBtn.addEventListener('click', () => { void printPdf(buildVoucherCardSheet(cards)); });
        actions.appendChild(printBtn);
        area.appendChild(actions);

        area.appendChild(createElement('p', {
            className: 'cid-pane-note',
            textContent: stored
                ? 'These codes are kept on THIS browser so the Delegates pane can offer them when '
                    + 'you invite someone. They are stored nowhere else — not on the server, not on '
                    + 'your other devices — and "Forget this device" deletes them. Print them.'
                : 'This browser is not keeping these codes (no local storage on this device), so '
                    + 'this screen is the only copy. Print them now.',
        }));
    }

    // ── Ledger ──────────────────────────────────────────────────────────────

    let offset = 0;

    const summaryEl = createElement('p', { className: 'cid-wizard-text' });
    listSection.appendChild(summaryEl);

    const tableWrap = createElement('div');
    tableWrap.style.overflowX = 'auto';
    listSection.appendChild(tableWrap);

    const pager = createElement('div', { className: 'cid-auth-link-row' });
    listSection.appendChild(pager);

    /**
     * "Check a voucher" (§7): type a code, see whether its BITS are still
     * parked and how many.
     *
     * This reads the WORKER, not the node — the node knows nothing about bits
     * (§1 idea 1), and the ledger above is the node's answer. The lock key is
     * derived here from the code, so an owner holding a code can always find
     * its money and nobody else can. The lock key itself is an IDENTIFIER: it
     * authorises reading and moves nothing (§4.2), which is why this is safe to
     * put in a pane at all.
     */
    function renderCheckVoucher(body: HTMLElement): void {
        body.appendChild(createElement('p', {
            className: 'cid-wizard-text',
            textContent: 'Paste a registration code to see whether its bits are still waiting. '
                + 'Nothing is sent to this server — the code stays on this device, and only the '
                + 'address it derives is looked up.',
        }));
        const field = createElement('div', { className: 'cid-auth-field' });
        const input = createElement('input', { className: 'cid-auth-input' }) as HTMLInputElement;
        input.type = 'text';
        input.placeholder = VOUCHER_CODE_PLACEHOLDER;
        input.autocomplete = 'off';
        input.spellcheck = false;
        field.appendChild(input);
        body.appendChild(field);

        const checkBtn = createElement('button', {
            className: 'cs-btn cs-btn--default cs-btn--small', textContent: 'Check',
        });
        body.appendChild(checkBtn);
        const result = createElement('p', { className: 'cid-pane-note' });
        body.appendChild(result);

        checkBtn.addEventListener('click', () => {
            const code = normalizeVoucherCode(input.value);
            if (!code) {
                result.textContent = 'That does not look like a registration code. Codes start with RGV-.';
                return;
            }
            checkBtn.setAttribute('disabled', '');
            result.textContent = 'Looking…';
            void (async () => {
                try {
                    const secrets = await deriveVoucherSecrets(code);
                    const lockKey = lockKeyFor(secrets.publicKey, 0);
                    const info = await bitcashWorker().lockInfo(lockKey);
                    if (!info.exists) {
                        result.textContent = 'No bits are parked for that code. Either it was never '
                            + 'paid for, or it is not a code from this operator.';
                    } else if (info.closed) {
                        result.textContent = info.closedReason === 'released' || info.closedReason === 'redeemed'
                            ? 'Those bits have been collected — either by the account that registered '
                                + 'with this code, or onto an invitation made from it.'
                            : `Those bits are no longer parked (${info.closedReason ?? 'closed'}).`;
                    } else {
                        result.textContent = `${unitsToBit(info.balance ?? '0')} BIT are waiting at `
                            + `${displayWalletKey(lockKey) ?? lockKey}. They reach a wallet when this `
                            + 'code is used to register, or move onto an invitation when it is '
                            + 'personalized.';
                    }
                } catch {
                    result.textContent = 'That code could not be checked just now.';
                } finally {
                    checkBtn.removeAttribute('disabled');
                }
            })();
        });
    }

    async function refresh(): Promise<void> {
        try {
            const page = await listOrganizationVouchers(orgCid!, { limit: PAGE_SIZE, offset });
            summaryEl.textContent = summaryLine(summarizeVouchers(page.vouchers));
            renderTable(page.vouchers);
            renderPager(page.total);
        } catch (e) {
            showError(formatApiError(e, 'Could not load the organization\'s vouchers'));
        }
    }

    function renderTable(rows: OrgVoucherSummary[]): void {
        tableWrap.innerHTML = '';
        if (rows.length === 0) {
            tableWrap.appendChild(createElement('p', {
                className: 'cid-wizard-text',
                textContent: 'This organization has not bought any delegate vouchers yet.',
            }));
            return;
        }

        const table = createElement('table');
        const head = createElement('tr');
        // No CODE column and no BITS column, and there never will be: the node
        // stores a hash of a lookup id and knows nothing about bits (§1). What
        // an organization sees here is STOCK — how many slots it holds and what
        // became of each — with the codes themselves on this browser and on
        // paper.
        for (const label of ['ID', 'Status', 'Invitation', 'Bought', 'Expires']) {
            head.appendChild(createElement('th', { textContent: label }));
        }
        table.appendChild(head);

        for (const v of rows) {
            const tr = createElement('tr');
            for (const cell of [
                String(v.voucherId),
                isExpired(v) ? 'expired' : lifecycleOf(v.status),
                // Filled by M15; until then every row reads the same.
                v.invitationId ?? '—',
                new Date(v.createdAt).toLocaleDateString(),
                new Date(v.expiresAt).toLocaleDateString(),
            ]) {
                tr.appendChild(createElement('td', { textContent: cell }));
            }
            table.appendChild(tr);
        }
        tableWrap.appendChild(table);
    }

    function renderPager(total: number): void {
        pager.innerHTML = '';
        if (total === 0) return;
        pager.appendChild(createElement('span', {
            className: 'cid-wizard-text',
            textContent: `${offset + 1}–${Math.min(offset + PAGE_SIZE, total)} of ${total}`,
        }));
        if (offset > 0) {
            const prev = createElement('a', { className: 'cid-auth-link', textContent: 'Previous' });
            prev.href = '#';
            prev.addEventListener('click', (e) => {
                e.preventDefault();
                offset = Math.max(0, offset - PAGE_SIZE);
                void refresh();
            });
            pager.appendChild(prev);
        }
        if (offset + PAGE_SIZE < total) {
            const next = createElement('a', { className: 'cid-auth-link', textContent: 'Next' });
            next.href = '#';
            next.addEventListener('click', (e) => {
                e.preventDefault();
                offset += PAGE_SIZE;
                void refresh();
            });
            pager.appendChild(next);
        }
    }

    function showError(message: string): void {
        statusEl.textContent = '';
        errorEl.textContent = message;
        errorEl.classList.add('cid-pane-error--visible');
        resetButton();
    }

    await refresh();
}
